import * as THREE from 'three';
import { loadGLTFModel, clearModelCache  } from './ModelLoader';
import { Debug } from '@shared/Debug';
import { LogCategory } from '@shared/Debug';

// Debug settings
const DEBUG_Y_POSITION = true; // Set to true to enable Y position debugging
const MINIMUM_ALLOWED_Y = 0.0; // Minimum allowed Y position (slightly above 0 for visibility)

/**
 * Class to handle GLB avatar animations and integration with the game
 */
export class GLBAvatarPlayer {
  private model: THREE.Group | null = null;
  private mixer: THREE.AnimationMixer | null = null;
  private animations: Map<string, THREE.AnimationAction> = new Map();
  private currentAnimation: string | null = null;
  private clock: THREE.Clock = new THREE.Clock();
  private rootBone: THREE.Object3D | null = null;
  private _isLoaded: boolean = false;
  private animationFiles: string[] = [];
  private debugSphere: THREE.Mesh | null = null; // Debug helper to visualize lowest point
  
  // Tracking for animation transitions
  private isInTransition: boolean = false;
  private transitionTimeout: any = null;
  private lastYPosition: number = 0;
  private lastRecoveryTime: number = 0; // Timestamp of last recovery attempt
  
  /**
   * Constructor
   * @param modelPath Path to the main GLB model file
   * @param animationFiles Array of paths to additional GLB files containing animations
   */
  constructor(
    private modelPath: string,
    animationFiles: string[] = []
  ) {
    this.modelPath = modelPath;
    this.animationFiles = animationFiles;
  }
  
  /**
   * Loads the model and its animations
   */
  private async loadModel(): Promise<void> {
    try {
      clearModelCache();
      // Load the main model first
      Debug.info(`Loading main model from: ${this.modelPath}`, LogCategory.PLAYER);
      const { model, animations } = await loadGLTFModel(this.modelPath);
      this.model = model;
      
      // Remove any lights that might be embedded in the model
      this.removeEmbeddedLights(this.model);
      
      // Adjust materials for better lighting
      this.model.traverse((object) => {
        if (object instanceof THREE.Mesh && object.material) {
          // Enable shadows
          object.castShadow = true;
          object.receiveShadow = true;
          
          if (Array.isArray(object.material)) {
            // Handle array of materials
            object.material.forEach(mat => {
              this.adjustMaterialLighting(mat);
            });
          } else {
            // Handle single material
            this.adjustMaterialLighting(object.material);
          }
        }
      });
      
      // Set up animation mixer with the model
      if (animations.length > 0) {
        this.mixer = new THREE.AnimationMixer(this.model);
        
        // Store all animations from the main model
        animations.forEach(clip => {
          // Apply a scale fix to the animation clip to match our model scale
          this.fixAnimationScale(clip);
          
          const action = this.mixer!.clipAction(clip);
          this.animations.set(clip.name, action);
        });
        
        Debug.info(`Loaded ${animations.length} animations from main model: ${animations.map(a => a.name).join(', ')}`, LogCategory.PLAYER);
      } else {
        Debug.warn('No animations found in the main GLB model', LogCategory.PLAYER);
      }
      
      // Load animations from additional files
      for (const file of this.animationFiles) {
        try {
          Debug.info(`Loading animations from: ${file}`, LogCategory.PLAYER);
          const { animations: additionalAnimations } = await loadGLTFModel(file);
          
          if (additionalAnimations.length > 0) {
            // Add each animation to the mixer
            additionalAnimations.forEach(clip => {
              // Apply scale fix for additional animations
              this.fixAnimationScale(clip);
              
              const action = this.mixer!.clipAction(clip);
              this.animations.set(clip.name, action);
            });
            
            Debug.info(`Loaded ${additionalAnimations.length} animations from ${file}: ${additionalAnimations.map(a => a.name).join(', ')}`, LogCategory.PLAYER);
          }
        } catch (error) {
          Debug.error(`Failed to load animations from ${file}: ${error}`, LogCategory.PLAYER);
        }
      }
      
      // Find the root bone that should correspond to the player's root
      this.rootBone = this.model;
      
      // Scale and position the model appropriately for the game
      this.model.scale.set(1, 1, 1); // Default scale - will be adjusted to fit
      
      // Center the model's pivot point
      this.centerModel();
      
      this._isLoaded = true;

    } catch (error) {
      Debug.error(`Failed to load GLB avatar: ${error}`, LogCategory.PLAYER);
      throw error; // Re-throw to handle it in the calling code
    }
  }
  
  /**
   * Removes any lights that might be embedded in the model
   * @param model The model to clean
   */
  private removeEmbeddedLights(model: THREE.Object3D): void {
    const lightsToRemove: THREE.Object3D[] = [];
    
    // Find all embedded lights
    model.traverse((object) => {
      if (
        object instanceof THREE.Light || 
        object.name.toLowerCase().includes('light') ||
        object.type.toLowerCase().includes('light')
      ) {
        Debug.warn(`Removing embedded light from avatar model: ${object.name} (${object.type})`, LogCategory.PLAYER);
        lightsToRemove.push(object);
      }
    });
    
    // Remove all found lights
    for (const light of lightsToRemove) {
      if (light.parent) {
        light.parent.remove(light);
      }
    }
    
    if (lightsToRemove.length > 0) {
      Debug.info(`Removed ${lightsToRemove.length} embedded lights from avatar model`, LogCategory.PLAYER);
    }
  }
  
  /**
   * Adjust material properties for better lighting
   */
  private adjustMaterialLighting(material: THREE.Material): void {
    if (material instanceof THREE.MeshStandardMaterial) {
      // Enhance standard material properties
      material.roughness = Math.min(material.roughness, 0.7);
      material.metalness = Math.min(material.metalness, 0.2);
      
      // Enhance the material's response to light
      material.envMapIntensity = 1.2;
      
      // Fix depth issues that can cause flickering
      material.depthWrite = true;
      material.depthTest = true;
      
      // Ensure normals are properly handled
      if (!material.normalMap) {
        material.flatShading = false;
      }
      
      // Slightly boost the emissive component for better visibility in darker areas
      if (!material.emissive.equals(new THREE.Color(0x000000))) {
        // If there's already an emissive component, enhance it slightly
        material.emissive.multiplyScalar(1.2);
      } else {
        // Add a very subtle ambient emissive to help with visibility
        material.emissive.set(0x111111);
      }
    } else if (material instanceof THREE.MeshPhongMaterial) {
      // Enhanced shininess for Phong materials
      material.shininess = 40;
      material.specular = new THREE.Color(0x444444);
      
      // Fix depth issues that can cause flickering
      material.depthWrite = true;
      material.depthTest = true;
    } else if (material instanceof THREE.MeshBasicMaterial) {
      // Convert basic materials to standard materials with better lighting
      const newMaterial = new THREE.MeshStandardMaterial({
        color: material.color,
        map: material.map,
        transparent: material.transparent,
        opacity: material.opacity,
        roughness: 0.7,
        metalness: 0.2
      });
      
      // Fix depth issues
      newMaterial.depthWrite = true;
      newMaterial.depthTest = true;
      
      // Note: the actual material replacement would need to happen at the mesh level
      // We're just preparing the material here
      material.copy(newMaterial);
    }
    
    // Set material side to double if needed
    if (material.transparent) {
      material.side = THREE.DoubleSide;
      
      // Ensure proper alpha test to prevent sorting issues with transparency
      if ('alphaTest' in material) {
        (material as any).alphaTest = 0.05;
      }
    } else {
      material.side = THREE.FrontSide;
    }
    
    // Enable color and lighting adjustments
    material.needsUpdate = true;
  }
  
  /**
   * Center the model based on its bounding box
   */
  private centerModel(): void {
    if (!this.model) return;
    
    Debug.info('Centering and scaling GLB model', LogCategory.PLAYER);
    
    // Create a bounding box for the model
    const boundingBox = new THREE.Box3().setFromObject(this.model);
    const center = new THREE.Vector3();
    boundingBox.getCenter(center);
    
    // Calculate model height for proper scaling
    const size = new THREE.Vector3();
    boundingBox.getSize(size);
    const modelHeight = size.y;
    
    // Calculate scale factor to make the model approximately 2 units tall
    const targetHeight = 2;
    const scaleFactor = targetHeight / modelHeight;
    
    // *** Add this logging ***
    Debug.info(`[centerModel] Model Path: ${this.modelPath}`, LogCategory.PLAYER);
    Debug.info(`[centerModel] Bounding Box Size (before scale): ${size}`, LogCategory.PLAYER);
    Debug.info(`[centerModel] Calculated modelHeight: ${modelHeight}`, LogCategory.PLAYER);
    Debug.info(`[centerModel] Calculated scaleFactor: ${scaleFactor}`, LogCategory.PLAYER);
    // *** End of logging ***
    
    // Apply scaling uniformly
    this.model.scale.set(scaleFactor, scaleFactor, scaleFactor);
    
    // Clear any previous adjustments
    this.model.position.set(0, 0, 0);
    
    // Recompute bounding box after scaling
    boundingBox.setFromObject(this.model);
    boundingBox.getCenter(center);
    
    // Center the model horizontally and adjust vertical position
    // This ensures the bottom of the model is at y=0
    this.model.position.x = -center.x;
    this.model.position.z = -center.z;
    
    // Fix the model orientation by rotating it 180 degrees around Y axis
    
    
    Debug.info(`Model centered and scaled. Scale factor: ${scaleFactor.toFixed(2)}`, LogCategory.PLAYER);
  }
  
  /**
   * Get the model root object
   * @returns The model's root THREE.Group
   */
  public async getModel(): Promise<THREE.Group | null> {
    try {
      // Wait for the model to load
      if (!this.model) {
        Debug.info(`[GLBAvatarPlayer] Loading model from ${this.modelPath}`, LogCategory.PLAYER);
        await this.loadModel();
      }

      if (!this.model) {
        Debug.error('[GLBAvatarPlayer] Model failed to load', LogCategory.PLAYER);
        return null;
      }

      // Ensure the idle animation is playing when the model is returned
      this.playIdleAnimation();
      
      return this.model;
    } catch (error) {
        Debug.error(`[GLBAvatarPlayer] Error in getModel: ${error}`, LogCategory.PLAYER);
      return null;
    }
  }
  
  /**
   * Method kept for reference - DO NOT USE
   * Avatar lighting should be handled by the scene, not attached to the avatar
   * @deprecated
   */
  private addAvatarLighting(container: THREE.Group): void {
    console.warn('addAvatarLighting is deprecated - lighting should be handled by the scene');
    // This method is kept for reference but should not be used
    // All lighting should be managed by the scene, not attached to individual avatars
  }
  
  /**
   * List all available animations in this model
   * @returns Array of animation names
   */
  public getAvailableAnimations(): string[] {
    return Array.from(this.animations.keys());
  }
  
  /**
   * Play a specific animation - simplified implementation without crossfade
   * @param name Name of the animation to play
   * @param loop Whether to loop the animation
   */
  public playAnimation(name: string, loop: boolean = true): void {
    try {
      // Skip if there's no mixer
      if (!this.mixer) {
        Debug.warn('No animation mixer available', LogCategory.PLAYER);
        return;
      }
      
      // Try common animation name variations if the exact name doesn't exist
      if (!this.animations.has(name)) {
        const variations = this.getAnimationVariations(name);
        let found = false;
        
        for (const variation of variations) {
          if (this.animations.has(variation)) {
            Debug.info(`Animation "${name}" not found, using variation: ${variation}`, LogCategory.PLAYER);
            name = variation;
            found = true;
            break;
          }
        }
        
        if (!found) {
          Debug.warn(`Animation "${name}" not found in model, using idle instead`, LogCategory.PLAYER);
          
          // Try to find an idle animation instead
          for (const idleName of ['idle', 'Idle', 'IDLE', 'stand', 'Stand']) {
            if (this.animations.has(idleName)) {
              name = idleName;
              found = true;
              break;
            }
          }
          
          // If still not found, just keep current animation
          if (!found) {
            return;
          }
        }
      }
      
      // If the animation is already playing, don't restart it
      if (this.currentAnimation === name) {
        return;
      }
      
      // Stop any currently playing animations
      this.mixer.stopAllAction();
      
      // Get the new action
      const action = this.animations.get(name)!;
      
      // Configure the action
      action.reset();
      action.loop = loop ? THREE.LoopRepeat : THREE.LoopOnce;
      action.clampWhenFinished = !loop;
      action.enabled = true;
      action.setEffectiveWeight(1);
      action.play();
      
      // Update current animation name
      this.currentAnimation = name;
      
      // No transitions to manage
      this.isInTransition = false;
      
      // Clear any existing timeout
      if (this.transitionTimeout) {
        clearTimeout(this.transitionTimeout);
        this.transitionTimeout = null;
      }
      
      Debug.info(`Playing animation: ${name} (loop: ${loop})`, LogCategory.PLAYER);
    } catch (error) {
      Debug.error(`Error in playAnimation: ${error}`, LogCategory.PLAYER);
      this.isInTransition = false;
      
      // If something goes wrong, ensure model Y position is valid
      if (this.model && this.model.position.y < 0) {
        this.model.position.y = 0;
        this.model.updateMatrix();
      }
    }
  }
  
  /**
   * Simplified emergency recovery method
   * Just plays idle animation with basic error handling
   */
  private tryRestoreIdleAnimation(): void {
    try {
      // Update recovery time
      this.lastRecoveryTime = performance.now();
      
      // Directly call playAnimation with 'idle'
      // Our simplified implementation will handle variations and fallbacks
      this.playAnimation('idle', true);
      
      // Ensure model is at valid Y position
      if (this.model && this.model.position.y < 0) {
        this.model.position.y = 0;
        this.model.updateMatrix();
      }
    } catch (error) {
      Debug.error(`Failed emergency animation recovery: ${error}`, LogCategory.PLAYER);
    }
  }
  
  /**
   * Get common variations of animation names
   * @param name Base animation name
   * @returns Array of possible animation name variations
   */
  private getAnimationVariations(name: string): string[] {
    const variations: string[] = [];

    // Common name variations
    switch (name.toLowerCase()) {
      case 'idle':
        variations.push('Idle', 'IDLE', 'idle_loop', 'Idle_Loop', 'idle_stance', 'stand', 'Stand');
        break;
      case 'run':
        variations.push('Run', 'RUN', 'run_loop', 'Run_Loop', 'running', 'walk', 'Walk', 'walking');
        break;
      case 'shoot':
        variations.push('Shoot', 'SHOOT', 'shooting', 'throw', 'Throw', 'basketball_shoot');
        break;
      case 'pass':
        variations.push('Pass', 'PASS', 'passing', 'throw_underhand', 'chest_pass');
        break;
      case 'block':
        variations.push('Block', 'BLOCK', 'blocking', 'jump', 'Jump', 'jumping', 'defense');
        break;
      case 'celebrate':
        variations.push('Celebrate', 'CELEBRATE', 'celebration', 'victory', 'happy', 'cheer');
        break;
      case 'defeat':
        variations.push('Defeat', 'DEFEAT', 'defeated', 'lose', 'sad', 'disappointed');
        break;
      default:
        // Just add capitalized versions
        variations.push(
          name.toUpperCase(),
          name.charAt(0).toUpperCase() + name.slice(1),
          name + '_loop',
          name + '_anim'
        );
    }
    
    return variations;
  }
  
  /**
   * Stop the current animation
   */
  public stopAnimation(): void {
    if (this.currentAnimation && this.animations.has(this.currentAnimation)) {
      const action = this.animations.get(this.currentAnimation)!;
      action.stop();
    }
    this.currentAnimation = null;
  }
  
  /**
   * Find the animation container in the hierarchy
   * @returns The animation container or null if not found
   */
  private findAnimationContainer(): THREE.Group | null {
    // With simplified structure, the model itself is the animation container
    return this.model;
  }
  
  /**
   * Update the animation (call in game loop)
   * @param deltaTime Time since last frame
   */
  public update(deltaTime?: number): void {
    if (!this.model || !this.mixer) return;
    
    // Calculate delta time if not provided
    const delta = deltaTime !== undefined ? deltaTime : this.clock.getDelta();
    
    // Store the original y position before update
    const originalY = this.model.position.y;
    this.lastYPosition = originalY;
    
    try {
      // Update the animation mixer
      this.mixer.update(delta);
      
      // Check if model has somehow dropped far below ground level during animation update
      // This is the catastrophic case where animations are broken
      if (this.model.position.y < -1.0) {
        Debug.error(`Model dropped severely below ground during animation update: ${this.model.position.y.toFixed(2)}`, LogCategory.PLAYER);
        this.model.position.y = Math.max(originalY, 0);
        this.model.updateMatrix();
        
        // Attempt to recover animation system
        if (this.isInTransition) {
          Debug.warn('Catastrophic animation failure during transition - resetting', LogCategory.PLAYER);
          this.isInTransition = false;
          if (this.transitionTimeout) {
            clearTimeout(this.transitionTimeout);
            this.transitionTimeout = null;
          }
          this.tryRestoreIdleAnimation();
        }
        
        // Skip further updates for this frame to prevent more issues
        return;
      }
      
      // Apply ground anchoring after animation update
      // Don't directly set Y position, but use incremental corrections
      if (originalY >= 0 && this.model.position.y < 0) {
        // Only correct when we go from valid to invalid position
        this.model.position.y = Math.max(originalY, 0);
        Debug.info(`Corrected Y position from ${this.model.position.y} to ${originalY}`, LogCategory.PLAYER);
      }
      
      // Update the model matrix
      this.model.updateMatrix();
      this.model.updateWorldMatrix(true, true);
      
      // We don't need continuous animation state checks anymore as they're causing infinite loops
      // The Player.setState method already handles animation state properly
      // and we have other safeguards for catastrophic cases
    } catch (error) {
      Debug.error(`Error updating animation: ${error}`, LogCategory.PLAYER);
      
      // Reset transition state if we encounter an error
      if (this.isInTransition) {
        Debug.warn('Animation error during transition - resetting transition state', LogCategory.PLAYER);
        this.isInTransition = false;
        if (this.transitionTimeout) {
          clearTimeout(this.transitionTimeout);
          this.transitionTimeout = null;
        }
      }
      
      // Restore original Y position if it was valid
      if (originalY >= 0 && this.model) {
        this.model.position.y = originalY;
        this.model.updateMatrix();
      }
      
      // Try to recover the animation with cooldown check
      const now = performance.now();
      const recoveryDelay = 2000; // 2 seconds between recovery attempts
      if (now - this.lastRecoveryTime >= recoveryDelay) {
        this.tryRestoreIdleAnimation();
      }
    }
    
    // Debug visualization
    if (DEBUG_Y_POSITION) {
      this.updateDebugVisualization();
    }
  }
  
  /**
   * Keeps the model properly anchored to the ground
   * @param originalY The original Y position before animation update
   */
  private applyGroundAnchoring(originalY: number = 0): void {
    if (!this.model) return;
    
    // Safety check - never allow originalY to be negative
    if (originalY < MINIMUM_ALLOWED_Y) {
      Debug.warn(`GLBAvatarPlayer: Correcting negative Y position (${originalY.toFixed(4)}) to ${MINIMUM_ALLOWED_Y}`, LogCategory.PLAYER);
      originalY = MINIMUM_ALLOWED_Y;
    }
    
    // Set a fixed base position that prevents floating
    const baseGroundY = MINIMUM_ALLOWED_Y;
    const maxAllowedHeight = 0.05; // Maximum height the character can float above ground
    
    // Check if this is a movement animation or transition
    const isMovementAnimation = this.currentAnimation && 
      (this.currentAnimation.toLowerCase().includes('run') || 
       this.currentAnimation.toLowerCase().includes('walk'));
    
    // For animation transitions, be extra careful with the height
    if (this.isInTransition) {
      // During transitions, maintain a consistent safe height
      this.model.position.y = Math.max(this.model.position.y, baseGroundY + 0.02);
    } else if (isMovementAnimation) {
      // For run animations, set a consistent, small offset from the ground
      // instead of continuously adding to the current position
      if (this.currentAnimation && this.currentAnimation.toLowerCase().includes('run')) {
        // Set an absolute position rather than a relative adjustment
        this.model.position.y = baseGroundY + maxAllowedHeight;
      } else {
        // For walking, keep closer to the ground
        this.model.position.y = baseGroundY + (maxAllowedHeight * 0.5);
      }
      
      // Check for bones that might be sinking below ground
      let lowestBoneY = Infinity;
      let lowestBoneName = "";
      
      // First pass: find the lowest bone
      this.model.traverse((object) => {
        if (object.type === 'Bone' && 
            (object.name.toLowerCase().includes('foot') || 
             object.name.toLowerCase().includes('leg') ||
             object.name.toLowerCase().includes('ankle'))) {
          
          // Get world position of the bone
          const worldPos = new THREE.Vector3();
          object.getWorldPosition(worldPos);
          
          // Track the lowest bone position
          if (worldPos.y < lowestBoneY) {
            lowestBoneY = worldPos.y;
            lowestBoneName = object.name;
          }
        }
      });
      
      // Log when bones go below ground level
      if (DEBUG_Y_POSITION && lowestBoneY < MINIMUM_ALLOWED_Y && lowestBoneY !== Infinity) {
        Debug.warn(`GLBAvatarPlayer: Bone "${lowestBoneName}" is below ground: ${lowestBoneY.toFixed(4)}`, LogCategory.PLAYER);
      }
      
      // If any bones are below minimum allowed Y, lift the model to compensate
      if (lowestBoneY < MINIMUM_ALLOWED_Y && lowestBoneY !== Infinity) {
        // Add a safety buffer to ensure it's above ground
        const adjustment = Math.abs(lowestBoneY) + 0.02;
        
        if (DEBUG_Y_POSITION) {
          Debug.info(`GLBAvatarPlayer: Adjusting model up by ${adjustment.toFixed(4)} units`, LogCategory.PLAYER);
        }
        
        this.model.position.y += adjustment;
      }
      
      // Ensure we're not exceeding the max allowed height
      if (this.model.position.y > (baseGroundY + maxAllowedHeight)) {
        this.model.position.y = baseGroundY + maxAllowedHeight;
      }
      
      // Update the model's matrix
      this.model.updateMatrix();
      this.model.updateWorldMatrix(true, true);
    } else {
      // For non-movement animations, maintain a minimum safe height
      this.model.position.y = Math.max(originalY, MINIMUM_ALLOWED_Y);
    }
    
    // Final safety check
    if (this.model.position.y < MINIMUM_ALLOWED_Y) {
      if (DEBUG_Y_POSITION) {
        Debug.error(`GLBAvatarPlayer: Model still below minimum Y: ${this.model.position.y.toFixed(4)}, forcing to ${MINIMUM_ALLOWED_Y}`, LogCategory.PLAYER);
      }
      this.model.position.y = MINIMUM_ALLOWED_Y;
      this.model.updateMatrix();
    }
  }
  
  /**
   * Add an event listener for when an animation completes
   * @param animationName Name of the animation to listen for completion
   * @param callback Function to call when animation completes
   */
  public onAnimationComplete(animationName: string, callback: () => void): void {
    if (!this.mixer || !this.animations.has(animationName)) {
      Debug.warn(`Animation "${animationName}" not found for event listener`, LogCategory.PLAYER);
      return;
    }
    
    const action = this.animations.get(animationName)!;
    
    // Set the animation to play once
    action.loop = THREE.LoopOnce;
    
    // Create a listener for the finished event
    const listener = (e: any) => {
      // Only trigger if this is the correct animation
      if (e.action === action) {
        callback();
        // Remove the listener after it fires once
        this.mixer!.removeEventListener('finished', listener);
      }
    };
    
    // Add the listener
    this.mixer.addEventListener('finished', listener);
  }
  
  /**
   * Properly dispose of all resources used by this GLBAvatarPlayer
   */
  public dispose(): void {
    // Stop all animations
    if (this.mixer) {
      this.mixer.stopAllAction();
      // Remove all listeners
      this.mixer.uncacheRoot(this.model);
    }

    // Clear animations map
    this.animations.clear();
    this.currentAnimation = null;
    
    // Clear any pending transitions
    if (this.transitionTimeout) {
      clearTimeout(this.transitionTimeout);
      this.transitionTimeout = null;
    }
    
    // Dispose of debug visualization if it exists
    if (this.debugSphere) {
      if (this.debugSphere.geometry) {
        this.debugSphere.geometry.dispose();
      }
      if (this.debugSphere.material) {
        if (Array.isArray(this.debugSphere.material)) {
          this.debugSphere.material.forEach(m => m.dispose());
        } else {
          this.debugSphere.material.dispose();
        }
      }
      if (this.debugSphere.parent) {
        this.debugSphere.parent.remove(this.debugSphere);
      }
      this.debugSphere = null;
    }
    
    // Don't dispose of the model itself, as it may be shared or managed by the parent
    this.model = null;
    this.mixer = null;
    this._isLoaded = false;
  }

  public isLoaded(): boolean {
    return this._isLoaded;
  }

  public hasModel(): boolean {
    return this.model !== null;
  }

  /**
   * Fix animation scales to match the model's scale
   * @param clip AnimationClip to fix
   */
  private fixAnimationScale(clip: THREE.AnimationClip): void {
    // Find if this is a movement animation
    const isMovementAnimation = clip.name.toLowerCase().includes('run') || 
                               clip.name.toLowerCase().includes('walk');
    
    // Special offset for run animations to prevent sinking
    const runYOffset = 0.05; // Small positive offset to prevent sinking
    
    // Handle potential scale mismatch between animations and model
    clip.tracks.forEach(track => {
      // Process position tracks
      if (track.name.includes('.position')) {
        const values = track.values;
        
        // For movement animations, we need special handling of root/hip bones
        if (isMovementAnimation) {
          // Calculate the average Y position first to see if we need to make adjustments
          let totalY = 0;
          let count = 0;
          for (let i = 1; i < values.length; i += 3) {
            totalY += values[i];
            count++;
          }
          const avgY = count > 0 ? totalY / count : 0;
          
          // If the average Y is negative, the animation will sink into the ground
          const needsLifting = avgY < 0;
          
          // Process each Y position value
          for (let i = 1; i < values.length; i += 3) { // Y values are at indexes 1, 4, 7, etc.
            // For running, we want to make sure Y is slightly positive rather than zero
            // This prevents the model from sinking into the ground
            if (needsLifting || values[i] < 0) {
              values[i] = runYOffset; // Apply a small lift to prevent sinking
            }
          }
        }
      }
      
      // Maintain normal rotations for most bones, but make sure they're normalized
      if (track.name.includes('.quaternion')) {
        const values = track.values;
        for (let i = 0; i < values.length; i += 4) {
          // Normalize quaternion (x,y,z,w)
          const x = values[i];
          const y = values[i + 1];
          const z = values[i + 2];
          const w = values[i + 3];
          const length = Math.sqrt(x*x + y*y + z*z + w*w);
          
          if (length !== 0) {
            values[i] = x / length;
            values[i + 1] = y / length;
            values[i + 2] = z / length;
            values[i + 3] = w / length;
          }
        }
      }
    });
  }

  /**
   * Plays the idle animation if not already playing
   * @returns True if successfully started playing idle animation
   */
  private playIdleAnimation(): boolean {
    // Don't try to play if we have no animations or mixer
    if (this.animations.size === 0 || !this.mixer) {
      return false;
    }
    
    // If already playing idle, don't restart
    if (this.currentAnimation && 
        (this.currentAnimation.toLowerCase().includes('idle') || 
         this.currentAnimation.toLowerCase().includes('stand'))) {
      return true;
    }
    
    // Try to find and play an idle animation
    for (const animName of ['idle', 'Idle', 'IDLE', 'idle_loop', 'stand', 'Stand']) {
      if (this.animations.has(animName)) {
        Debug.info(`Starting default idle animation: ${animName}`, LogCategory.PLAYER);
        this.playAnimation(animName, true);
        return true;
      }
    }
    
    // If no specific idle animation found, try to find an animation with 'idle' in the name
    for (const animName of Array.from(this.animations.keys())) {
      if (animName.toLowerCase().includes('idle') || animName.toLowerCase().includes('stand')) {
        Debug.info(`Starting idle animation by name match: ${animName}`, LogCategory.PLAYER);
        this.playAnimation(animName, true);
        return true;
      }
    }
    
    // If all else fails, just play the first animation
    if (this.animations.size > 0) {
      const firstAnim = Array.from(this.animations.keys())[0];
      Debug.info(`No idle animation found, playing first available: ${firstAnim}`, LogCategory.PLAYER);
      this.playAnimation(firstAnim, true);
      return true;
    }
    
    Debug.warn('No animations available to play as idle', LogCategory.PLAYER);
    return false;
  }

  /**
   * Strict enforcement of minimum height - fail-safe to prevent sinking
   */
  private enforceMinimumHeight(): void {
    if (!this.model) return;
    
    // Check model position
    if (this.model.position.y < MINIMUM_ALLOWED_Y) {
      if (DEBUG_Y_POSITION) {
        Debug.info(`GLBAvatarPlayer: Enforcing minimum height. Y was ${this.model.position.y.toFixed(4)}`, LogCategory.PLAYER);
      }
      this.model.position.y = MINIMUM_ALLOWED_Y;
      this.model.updateMatrix();
      this.model.updateWorldMatrix(true, true);
    }
    
    // Find lowest point for repositioning check
    let lowestPoint = Infinity;
    let lowestName = "";
    
    // First pass: find the lowest point in the model
    this.model.traverse((object) => {
      if (object.type === 'Bone' || object instanceof THREE.Mesh) {
        const worldPos = new THREE.Vector3();
        object.getWorldPosition(worldPos);
        
        if (worldPos.y < lowestPoint) {
          lowestPoint = worldPos.y;
          lowestName = object.name;
        }
      }
    });
    
    // If the model has dropped significantly below the floor, something went wrong with the animation
    // This is the catastrophic case we're trying to fix
    if (lowestPoint < -1.0) {
      Debug.error(`GLBAvatarPlayer: Model has dropped severely below floor (${lowestPoint.toFixed(2)}). Resetting position and animations.`, LogCategory.PLAYER);
      
      // Reset model position to safe values
      this.model.position.y = MINIMUM_ALLOWED_Y;
      
      // Reset transition state
      this.isInTransition = false;
      if (this.transitionTimeout) {
        clearTimeout(this.transitionTimeout);
        this.transitionTimeout = null;
      }
      
      // Stop all animations and restart idle
      if (this.mixer) {
        this.mixer.stopAllAction();
        
        // Try to restart idle animation if possible
        if (this.currentAnimation) {
          const currentAnim = this.animations.get(this.currentAnimation);
          if (currentAnim) {
            currentAnim.reset();
            currentAnim.play();
          } else {
            // If we can't restart current animation, try to find idle
            this.playIdleAnimation();
          }
        } else {
          this.playIdleAnimation();
        }
      }
      
      // Force matrix updates
      this.model.updateMatrix();
      this.model.updateWorldMatrix(true, true);
      
      return; // Exit after handling catastrophic case
    }
    
    // Handle normal small adjustments if any part is below ground
    let needsUpdate = false;
    
    if (lowestPoint < MINIMUM_ALLOWED_Y) {
      // Calculate how much to lift the model
      const liftAmount = Math.abs(lowestPoint - MINIMUM_ALLOWED_Y) + 0.01; // Add a small buffer
      
      // Lift the model up by the calculated amount
      this.model.position.y += liftAmount;
      
      if (DEBUG_Y_POSITION) {
        Debug.warn(`GLBAvatarPlayer: Lowest point "${lowestName}" was at ${lowestPoint.toFixed(4)}, lifting by ${liftAmount.toFixed(4)}`, LogCategory.PLAYER);
      }
      
      needsUpdate = true;
    }
    
    // If anything needed updating, ensure matrices are updated
    if (needsUpdate) {
      this.model.updateMatrix();
      this.model.updateWorldMatrix(true, true);
    }
  }
  
  /**
   * Create or update debug visualization
   */
  private updateDebugVisualization(): void {
    if (!this.model) return;
    
    // Find lowest point for visualization
    let lowestPoint = Infinity;
    let lowestPosition = new THREE.Vector3();
    
    this.model.traverse((object) => {
      if ((object.type === 'Bone' && 
          (object.name.toLowerCase().includes('foot') || 
           object.name.toLowerCase().includes('ankle'))) || 
          object instanceof THREE.Mesh) {
        
        const worldPos = new THREE.Vector3();
        object.getWorldPosition(worldPos);
        
        if (worldPos.y < lowestPoint) {
          lowestPoint = worldPos.y;
          lowestPosition.copy(worldPos);
        }
      }
    });
    
    // If we found a lowest point and it's below the threshold, show debug object
    if (lowestPoint < MINIMUM_ALLOWED_Y) {
      // Create debug sphere if it doesn't exist
      if (!this.debugSphere) {
        const geometry = new THREE.SphereGeometry(0.05, 8, 8);
        const material = new THREE.MeshBasicMaterial({ 
          color: 0xff0000, 
          wireframe: true,
          transparent: true,
          opacity: 0.8
        });
        this.debugSphere = new THREE.Mesh(geometry, material);
        this.model.add(this.debugSphere);
      }
      
      // Update debug sphere position
      if (this.debugSphere) {
        this.debugSphere.position.copy(lowestPosition);
        this.debugSphere.visible = true;
      }
    } else if (this.debugSphere) {
      // Hide debug sphere when everything is above ground
      this.debugSphere.visible = false;
    }
  }
}